import type { ReactElement } from 'react';
import type { IntlShape } from 'react-intl-next';

import { i18n } from './quickReloadi18n';
import { getCommentPreview } from './CommentPreview';
import type { CommentPreviewProps } from './CommentPreview';
import { getCommentFlagMessage } from './quickReloadHelpers';
import { getUniqueUsers, type UserInfo } from './pubSubUtils';

/**
 * A stream manager that accepts the edits and comments events received by
 * the poller, stores them, and initiates the display of flags with
 * appropriately localized messages.
 */

export type StreamEvent = {
	displayName: string;
	profilePicturePath: string;
	accountId: string | null;
	url?: string | null;
	markerRef?: string | null;
	commentId?: string | null;
	adfBodyContent?: string;
	commentType?: string;
	isInline?: boolean;
	commentCreatedDate?: string;
};

export type FlagType = 'batch' | 'single';

export type FlagSpec = {
	title: string;
	numEvents: number;
	description: string | ReactElement;
	onClose: () => void;
	type: FlagType;
	commentType: string;
	commentIsAReply: boolean;
	commentId?: string;
	url?: string;
	markerRef?: string;
	commentCreatedDate?: string;
	uniqueUsers: UserInfo[]; // represents the unique user count in the incoming events list, does not include current user
};

type MessageProps = {
	commentPreviewInfo: CommentPreviewProps;
	commentType?: string;
	editorSynchronousCommentsEnabled?: boolean;
	commentCreatedDate?: string;
	uniqueUsersCount: number;
	getDocumentNeedsUpdate?: () => boolean;
};

abstract class Stream {
	numEvents: number = 0;
	// represents all the events created by other users, does not include current user
	eventsExcludingCurrentUserEvents: StreamEvent[] = [];

	abstract createMessage({
		commentPreviewInfo,
		commentType,
		commentCreatedDate,
		uniqueUsersCount,
		getDocumentNeedsUpdate,
	}: MessageProps): FlagSpec;

	/* eslint-disable no-useless-constructor */
	constructor(
		public intl: IntlShape,
		public currentAccountId: string | null,
		public contentType: string,
		public showFlag: (FlagSpec) => Promise<void>,
		public getDocumentNeedsUpdate?: () => boolean,
	) {
		/**
		 * eslint mistakenly thinks this is a useless constructor. It's not,
		 * because TypeScript translates the 'public' arguments to actual code that
		 * assigns them to instance variables.
		 */
	}

	getOtherUserEvents() {
		return this.eventsExcludingCurrentUserEvents;
	}

	addEvents(streamEvents: Array<StreamEvent>) {
		const incomingEventsExcludingCurrentUser = streamEvents.filter((streamEvent) => {
			return (
				streamEvent.accountId &&
				this.currentAccountId &&
				streamEvent.accountId !== this.currentAccountId
			);
		});

		if (incomingEventsExcludingCurrentUser.length === 0) {
			return;
		}

		// for pubsub, streamEvents will only contain one event
		// if the flag is still open, then this.eventsExcludingCurrentUserEvents will contain previous events
		this.eventsExcludingCurrentUserEvents = [
			...this.eventsExcludingCurrentUserEvents,
			...incomingEventsExcludingCurrentUser,
		];

		// don't show the flag if no events were created by other users
		if (this.eventsExcludingCurrentUserEvents.length === 0) {
			return;
		}

		const mostRecentComment =
			this.eventsExcludingCurrentUserEvents[this.eventsExcludingCurrentUserEvents.length - 1];
		const commentIsAReply = mostRecentComment.commentType === 'REPLY';
		const commentType = mostRecentComment.isInline
			? commentIsAReply
				? 'inline-reply'
				: 'inline'
			: 'page';

		const uniqueUsers = getUniqueUsers(this.eventsExcludingCurrentUserEvents);

		// this is used for both comments and page publishes, page publishes don't use the comment related info specified here
		const flagSpecMessage: FlagSpec = this.createMessage({
			commentPreviewInfo: {
				currentCommentADF: mostRecentComment.adfBodyContent ?? '',
				commentIsAReply,
			},
			commentType,
			commentCreatedDate: mostRecentComment.commentCreatedDate,
			uniqueUsersCount: uniqueUsers.length,
			getDocumentNeedsUpdate: this.getDocumentNeedsUpdate,
		});
		if (mostRecentComment.url) {
			flagSpecMessage.url = mostRecentComment.url || undefined;
		}
		if (mostRecentComment.markerRef) {
			flagSpecMessage.markerRef = mostRecentComment.markerRef || undefined;
		}
		if (mostRecentComment.commentId) {
			flagSpecMessage.commentId = mostRecentComment.commentId || undefined;
		}

		flagSpecMessage.commentIsAReply = commentIsAReply;
		flagSpecMessage.uniqueUsers = uniqueUsers;

		void this.showFlag(flagSpecMessage);
	}

	resetEvents = () => {
		this.numEvents = 0;
		this.eventsExcludingCurrentUserEvents = [];
	};
}

export class EditsStream extends Stream implements Stream {
	createMessage({ uniqueUsersCount }: MessageProps): FlagSpec {
		const { eventsExcludingCurrentUserEvents, contentType, intl, resetEvents } = this;
		const flagProps = {
			title: intl.formatMessage(i18n.newVersion),
			onClose: resetEvents,
			numEvents: this.numEvents,
		} as FlagSpec;

		const somebody =
			eventsExcludingCurrentUserEvents[eventsExcludingCurrentUserEvents.length - 1]?.displayName;
		const somebodyElse: string | undefined =
			eventsExcludingCurrentUserEvents[eventsExcludingCurrentUserEvents.length - 2]?.displayName;
		const numOthers = eventsExcludingCurrentUserEvents.length - 1;
		const isBlog = contentType === 'blogpost';

		// The exact messaging varies based on the # of unique users who have made
		// edits as well as the type of content (page or blogpost) that was edited

		if (uniqueUsersCount === 1) {
			// somebody published a new version...
			const message = isBlog ? i18n.somebodyPublishedBlog : i18n.somebodyPublished;
			return {
				...flagProps,
				description: intl.formatMessage(message, { somebody }),
				type: 'single',
			};
		} else if (uniqueUsersCount === 2) {
			// somebody and somebodyElse published a new version...
			const message = isBlog ? i18n.twoPeoplePublishedBlog : i18n.twoPeoplePublished;
			return {
				...flagProps,
				description: intl.formatMessage(message, { somebody, somebodyElse }),
				type: 'batch',
			};
		} else {
			// somebody and numOthers published a new version...
			const message = isBlog
				? i18n.atLeastThreePeoplePublishedBlog
				: i18n.atLeastThreePeoplePublished;
			return {
				...flagProps,
				description: intl.formatMessage(message, { somebody, numOthers }),
				type: 'batch',
			};
		}
	}
}

export class CommentsStream extends Stream {
	createMessage({
		commentPreviewInfo,
		commentType,
		commentCreatedDate,
		uniqueUsersCount,
		getDocumentNeedsUpdate,
	}: MessageProps): FlagSpec {
		const commentTypeText = commentPreviewInfo.commentIsAReply ? 'reply' : 'comment';

		const documentNeedsUpdate = getDocumentNeedsUpdate && getDocumentNeedsUpdate();

		const ret = {
			onClose: this.resetEvents,
			numEvents: this.numEvents,
			title: getCommentFlagMessage(
				this.eventsExcludingCurrentUserEvents,
				uniqueUsersCount,
				this.intl,
				commentTypeText,
				commentCreatedDate,
				documentNeedsUpdate,
			),
			commentType,
		} as FlagSpec;

		return {
			...ret,
			description: getCommentPreview(commentPreviewInfo),
			type: uniqueUsersCount === 1 ? 'single' : 'batch',
		};
	}
}
